import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, UserPlus, MoreVertical, Trash2, UserMinus, Shield, User as UserIcon, Mail, Calendar } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/shared/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Separator } from "@/shared/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { SubscriptionRequiredScreen } from "@/features/billing/components/SubscriptionRequiredScreen";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/apiClient";
import { useRequireAdmin } from "@/shared/hooks/useRoleGuard";

interface TeamMember {
  id: string;
  user_id: string;
  role: 'user' | 'admin';
  email?: string;
  name?: string;
  avatar_url?: string | null;
}

interface PendingInvite {
  id: string;
  email: string;
  role: 'user' | 'admin';
  invited_at: string;
}

type MixedItem =
  | (TeamMember & { type: 'member' })
  | (PendingInvite & { type: 'invite' });

export default function TeamManagementPage() {
  // Admin-only access guard
  useRequireAdmin();

  const navigate = useNavigate();
  const { toast } = useToast();
  const { canAccess, reason, checkAccess } = useSubscriptionGuard();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [userRole, setUserRole] = useState<'user' | 'admin' | ''>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [maxSeats, setMaxSeats] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MixedItem | null>(null);

  // Forms
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'user' | 'admin'>('user');
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState(false);

  // Alert dialogs
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [showRevokeAlert, setShowRevokeAlert] = useState(false);
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');

  useEffect(() => {
    loadTeamData();
  }, []);

  useEffect(() => {
    if (selectedItem && selectedItem.type === 'member') {
      setEditRole(selectedItem.role);
    }
  }, [selectedItem]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      // SECURITY: Get current user role + company via backend API
      const userResponse = await apiClient.get<{
        ok: boolean;
        user: any;
        userRole: { role: string; company_id: string } | null;
      }>('/user/me');

      if (!userResponse.ok || !userResponse.user || !userResponse.userRole) {
        setUserRole('');
        setCompanyId('');
        setMembers([]);
        setPendingInvites([]);
        setMaxSeats(null);
        return;
      }

      setUserRole(userResponse.userRole.role as 'user' | 'admin');
      setCompanyId(userResponse.userRole.company_id);

      // SECURITY: Get subscription details via backend API
      const subResponse = await apiClient.get<{
        ok: boolean;
        subscription: any;
      }>('/user/subscription');
      setMaxSeats(subResponse.ok ? (subResponse.subscription?.user_count ?? null) : null);

      // SECURITY: Get team members via backend API
      const membersResponse = await apiClient.get<{
        ok: boolean;
        members: Array<{
          id: string;
          user_id: string;
          role: 'user' | 'admin';
          email: string;
          name: string;
          avatar_url: string | null;
        }>;
      }>('/team/members');

      const membersWithDetails: TeamMember[] = membersResponse.ok
        ? membersResponse.members
        : [];

      // SECURITY: Get pending invites via backend API
      const invitesResponse = await apiClient.get<{
        ok: boolean;
        invites: Array<{
          id: string;
          email: string;
          role: 'user' | 'admin';
          invited_at: string;
        }>;
      }>('/team/invites');

      setMembers(membersWithDetails);
      setPendingInvites(invitesResponse.ok ? invitesResponse.invites : []);
    } catch (error) {
      console.error('Error loading team data:', error);
      toast({
        title: "Error",
        description: "Failed to load team data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadTeamData();
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const adminCount = useMemo(
    () => members.filter((m) => m.role === 'admin').length,
    [members]
  );

  const filteredData = useMemo(() => {
    const allItems: MixedItem[] = [
      ...members.map((m) => ({ ...m, type: 'member' as const })),
      ...pendingInvites.map((i) => ({ ...i, type: 'invite' as const })),
    ];
    return allItems.filter((item) => {
      if (!searchTerm) return true;
      const email = 'email' in item ? item.email : '';
      const name = 'name' in item ? item.name : '';
      return (
        email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [members, pendingInvites, searchTerm]);

  const handleInvite = async () => {
    const trimmedEmail = inviteEmail.trim().toLowerCase();

    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // SUBSCRIPTION GUARD: Check subscription first, before seat limits
    if (!checkAccess('Team Invites')) {
      return; // checkAccess handles toast and navigation
    }

    if (!companyId) {
      toast({
        title: "Error",
        description: "Company not resolved. Try reloading.",
        variant: "destructive",
      });
      return;
    }

    // Check seats (after subscription check)
    // Count both active members AND pending invites to prevent over-inviting
    const totalMembers = members.length;
    const totalPendingInvites = pendingInvites.length;
    const totalOccupied = totalMembers + totalPendingInvites;

    if (maxSeats !== null && totalOccupied >= maxSeats) {
      toast({
        title: "Seat Limit Reached",
        description: `You have ${totalMembers} active member${totalMembers !== 1 ? 's' : ''} and ${totalPendingInvites} pending invite${totalPendingInvites !== 1 ? 's' : ''} (${totalOccupied}/${maxSeats} seats used). Please upgrade your plan to add more team members.`,
        variant: "destructive",
      });
      setUpgradeEmail(trimmedEmail);
      setShowUpgradeAlert(true);
      return;
    }

    setIsSaving(true);
    try {
      console.log('[TeamManagement] Sending invite:', { companyId, email: trimmedEmail, role: inviteRole });

      // Get auth token for backend request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const SIGNALING_SERVER_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';

      // Call backend API
      const response = await fetch(`${SIGNALING_SERVER_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          email: trimmedEmail,
          role: inviteRole,
        }),
      });

      const data = await response.json();
      console.log('[TeamManagement] Invite response:', { status: response.status, data });

      if (!response.ok) {
        // Handle specific error cases
        if (data.needsUpgrade) {
          console.log('[TeamManagement] Needs upgrade');
          setUpgradeEmail(trimmedEmail);
          setShowUpgradeAlert(true);
          setIsSaving(false);
          return;
        }

        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      console.log('[TeamManagement] ✅ Invite sent successfully');
      setInviteEmail('');
      setInviteRole('user');
      setShowInviteModal(false);
      await loadTeamData();

      toast({
        title: "Success",
        description: "Invitation sent successfully",
      });
    } catch (error: any) {
      console.error('[TeamManagement] Error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemClick = (item: MixedItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const handleRevokeInvite = async () => {
    if (!selectedItem || selectedItem.type !== 'invite') return;

    setIsSaving(true);

    try {
      // SECURITY: Use backend API to cancel invite
      const response = await apiClient.delete<{ ok: boolean }>(
        `/team/invites/${selectedItem.id}`
      );

      if (!response.ok) throw new Error('Failed to cancel invitation');

      setShowRevokeAlert(false);
      setShowDetailModal(false);
      await loadTeamData();

      toast({
        title: "Success",
        description: "Invitation cancelled",
      });
    } catch (error: any) {
      console.error('[TeamManagement] Error revoking invite:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel invitation",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedItem || selectedItem.type !== 'member') return;

    if (selectedItem.user_id === currentUserId) {
      toast({
        title: "Not allowed",
        description: "You cannot remove yourself from the team.",
        variant: "destructive",
      });
      return;
    }

    if (selectedItem.role === 'admin' && adminCount <= 1) {
      toast({
        title: "Not allowed",
        description: "At least one admin must remain in the team.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      console.log('[TeamManagement] Removing member:', selectedItem.user_id);

      // Get auth token for backend request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const SIGNALING_SERVER_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';

      // Call backend API
      const response = await fetch(`${SIGNALING_SERVER_URL}/api/team/remove-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          member_user_id: selectedItem.user_id,
        }),
      });

      const data = await response.json();
      console.log('[TeamManagement] Remove member response:', { status: response.status, data });

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      setShowRemoveAlert(false);
      setShowDetailModal(false);
      await loadTeamData();

      toast({
        title: "Success",
        description: "Team member removed",
      });
    } catch (error: any) {
      console.error('[TeamManagement] Remove member error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove member",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedItem || selectedItem.type !== 'member') return;
    if (!companyId) return;

    if (selectedItem.role === editRole) {
      toast({
        title: "No changes",
        description: `Already ${editRole}.`,
      });
      return;
    }

    const isSelf = selectedItem.user_id === currentUserId;
    if (selectedItem.role === 'admin' && editRole === 'user') {
      if (adminCount <= 1) {
        toast({
          title: "Not allowed",
          description: "At least one admin must remain in the team.",
          variant: "destructive",
        });
        return;
      }
      if (isSelf && adminCount <= 1) {
        toast({
          title: "Not allowed",
          description: "At least one admin must remain in the team.",
          variant: "destructive",
        });
        return;
      }
    }

    setRoleUpdating(true);
    try {
      // SECURITY: Use backend API to update member role
      const response = await apiClient.put<{ ok: boolean }>(
        `/team/members/${selectedItem.user_id}/role`,
        { role: editRole }
      );

      if (!response.ok) throw new Error('Failed to update role');

      setSelectedItem((prev) =>
        prev && prev.type === 'member' && prev.user_id === selectedItem.user_id
          ? { ...prev, role: editRole }
          : prev
      );
      setMembers((prev) =>
        prev.map((m) => (m.user_id === selectedItem.user_id ? { ...m, role: editRole } : m))
      );

      toast({
        title: "Success",
        description: `Access updated to ${editRole === 'admin' ? 'Admin' : 'User'}.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setRoleUpdating(false);
    }
  };

  const isAdmin = userRole === 'admin';

  if (loading && !userRole) {
    return (
      <div className="container mx-auto p-6 max-w-6xl flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading team...</p>
        </div>
      </div>
    );
  }

  // Check subscription access for admins (non-admins already blocked above)
  if (userRole === 'admin' && !canAccess) {
    return (
      <SubscriptionRequiredScreen
        feature="Team Management"
        reason={reason}
        onUpgrade={() => navigate('/profile?tab=billing')}
      />
    );
  }

  if (userRole !== 'admin') {
    return (
      <div className="container mx-auto p-6 max-w-6xl flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to view this page. Ask an admin to grant you access.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Team Management</h1>
        <p className="text-muted-foreground">
          {loading
            ? 'Loading...'
            : `${members.length} of ${maxSeats ?? '—'} seats used`}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{members.length}</div>
            {maxSeats && (
              <p className="text-xs text-muted-foreground mt-1">
                of {maxSeats} seats used
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingInvites.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search team members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </div>

      {/* Team List */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members & Invitations</CardTitle>
          <CardDescription>
            {filteredData.length} {filteredData.length === 1 ? 'person' : 'people'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading team...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchTerm ? 'No members found' : 'No team members yet'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm
                  ? 'Try adjusting your search'
                  : 'Invite team members to collaborate'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredData.map((item) => {
                const isMember = item.type === 'member';
                const displayName = isMember ? item.name || item.email : item.email;

                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleItemClick(item)}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        {isMember && item.avatar_url ? (
                          <AvatarImage src={item.avatar_url} />
                        ) : null}
                        <AvatarFallback>
                          {isMember ? getInitials(displayName || 'U') : <UserPlus className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{displayName}</p>
                        {isMember ? (
                          item.email && item.name ? (
                            <p className="text-sm text-muted-foreground">{item.email}</p>
                          ) : null
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Invited as {item.role} • Pending
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant={
                        isMember
                          ? item.role === 'admin'
                            ? 'default'
                            : 'secondary'
                          : 'outline'
                      }
                    >
                      {isMember
                        ? item.role === 'admin'
                          ? 'Admin'
                          : 'User'
                        : 'Pending'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <RadioGroup value={inviteRole} onValueChange={(value: 'user' | 'admin') => setInviteRole(value)}>
                <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                  <RadioGroupItem value="user" id="user" />
                  <div className="flex-1">
                    <Label htmlFor="user" className="font-normal cursor-pointer">
                      <div className="font-medium">User</div>
                      <div className="text-sm text-muted-foreground">Standard access</div>
                    </Label>
                  </div>
                </div>
                <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                  <RadioGroupItem value="admin" id="admin" />
                  <div className="flex-1">
                    <Label htmlFor="admin" className="font-normal cursor-pointer">
                      <div className="font-medium">Admin</div>
                      <div className="text-sm text-muted-foreground">Full access + billing</div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm text-muted-foreground">
                An invitation email will be sent to this address. They'll automatically join your team when they sign up.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isSaving}>
              {isSaving ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal - Member */}
      {selectedItem?.type === 'member' && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Member Details</DialogTitle>
            </DialogHeader>

            {/* Header with Avatar */}
            <div className="flex flex-col items-center text-center py-6">
              <Avatar className="h-20 w-20 mb-4">
                {selectedItem.avatar_url ? (
                  <AvatarImage src={selectedItem.avatar_url} />
                ) : null}
                <AvatarFallback className="text-2xl">
                  {getInitials(selectedItem.name || selectedItem.email || 'U')}
                </AvatarFallback>
              </Avatar>
              <h3 className="text-lg font-semibold">
                {selectedItem.name || selectedItem.email}
              </h3>
              {selectedItem.email && selectedItem.name && (
                <p className="text-sm text-muted-foreground">{selectedItem.email}</p>
              )}
              <div className="mt-2">
                <Badge variant={selectedItem.role === 'admin' ? 'default' : 'secondary'}>
                  {selectedItem.role === 'admin' ? 'Admin' : 'User'}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Access Level Section */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">Access Level</h4>
                <p className="text-sm text-muted-foreground">
                  Control what this member can do in your workspace.
                </p>
              </div>

              <RadioGroup
                value={editRole}
                onValueChange={(value: 'user' | 'admin') => setEditRole(value)}
                disabled={selectedItem.user_id === currentUserId}
              >
                <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                  <RadioGroupItem value="user" id="edit-user" />
                  <div className="flex-1">
                    <Label htmlFor="edit-user" className="font-normal cursor-pointer">
                      <div className="font-medium">User</div>
                      <div className="text-sm text-muted-foreground">Standard access</div>
                    </Label>
                  </div>
                </div>
                <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                  <RadioGroupItem value="admin" id="edit-admin" />
                  <div className="flex-1">
                    <Label htmlFor="edit-admin" className="font-normal cursor-pointer">
                      <div className="font-medium">Admin</div>
                      <div className="text-sm text-muted-foreground">Full access + billing</div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>

              {selectedItem.user_id !== currentUserId && selectedItem.role !== editRole && (
                <Button
                  onClick={handleUpdateRole}
                  disabled={roleUpdating}
                  className="w-full"
                >
                  {roleUpdating ? "Updating..." : "Update Access"}
                </Button>
              )}

              {selectedItem.user_id === currentUserId && (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    You can't change your own access level.
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Remove Member */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">Remove Member</h4>
                <p className="text-sm text-muted-foreground">
                  This will immediately revoke {selectedItem.name || selectedItem.email}'s access to your workspace. They can be re-invited later.
                </p>
              </div>

              <Button
                variant="destructive"
                onClick={() => setShowRemoveAlert(true)}
                disabled={
                  selectedItem.user_id === currentUserId ||
                  (selectedItem.role === 'admin' && adminCount <= 1)
                }
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove from Team
              </Button>

              {(selectedItem.user_id === currentUserId ||
                (selectedItem.role === 'admin' && adminCount <= 1)) && (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    At least one admin must remain in the team.
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Detail Modal - Pending Invitation */}
      {selectedItem?.type === 'invite' && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Pending Invitation</DialogTitle>
            </DialogHeader>

            {/* Header */}
            <div className="flex flex-col items-center text-center py-6">
              <Avatar className="h-20 w-20 mb-4">
                <AvatarFallback className="text-2xl">
                  <Mail className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              <h3 className="text-lg font-semibold">{selectedItem.email}</h3>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline">Pending</Badge>
                <Badge variant={selectedItem.role === 'admin' ? 'default' : 'secondary'}>
                  {selectedItem.role === 'admin' ? 'Admin' : 'User'}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Invitation Details */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">Invitation Details</h4>
                <div className="bg-muted p-3 rounded-md mb-3">
                  <p className="text-sm text-muted-foreground">
                    An invitation email was sent to this address.
                  </p>
                </div>

                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Sent</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedItem.invited_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium">Role</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedItem.role === 'admin'
                        ? 'Admin (Full access + billing)'
                        : 'User (Standard access)'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Cancel Invitation */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">Cancel Invitation</h4>
                <p className="text-sm text-muted-foreground">
                  This will revoke the invitation. You can send a new one later if needed.
                </p>
              </div>

              <Button
                variant="destructive"
                onClick={() => setShowRevokeAlert(true)}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cancel Invitation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Remove Member Alert */}
      <AlertDialog open={showRemoveAlert} onOpenChange={setShowRemoveAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {selectedItem?.type === 'member' ? selectedItem.name || selectedItem.email : ''} from the team?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSaving ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Alert */}
      <AlertDialog open={showRevokeAlert} onOpenChange={setShowRevokeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel invitation for {selectedItem?.type === 'invite' ? selectedItem.email : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeInvite}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Alert */}
      <AlertDialog open={showUpgradeAlert} onOpenChange={setShowUpgradeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>All seats in use</AlertDialogTitle>
            <AlertDialogDescription>
              You've used all {maxSeats} seats.
              {'\n\n'}
              Would you like to invite {upgradeEmail} as a user for an additional $49.99/mo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowInviteModal(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowUpgradeAlert(false);
                setShowInviteModal(false);
                navigate('/profile?tab=billing');
              }}
            >
              Invite for $49.99/mo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
